import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useGetNavigationItems, useGetPages, useGetPlatformBySubdomain } from '@juno/client-api';
import { Page } from '@juno/client-api/utils';
import { JunoSpin } from '@juno/ui';

// This component handles root domain (/) navigation by determining
// the appropriate default page based on the current subdomain.
// It fetches the platform associated with the subdomain, identifies its default site,
// and redirects users to either the 'home' page or the first available internal page
// in the navigation of that site.
const DefaultRedirect: React.FC = () => {
  const navigate = useNavigate();
  const { hostname } = window.location;
  const subdomain = hostname.split('.')[0];

  // Get platform info to find the default site
  const { data: platform, isLoading: platformLoading } = useGetPlatformBySubdomain(
    subdomain,
    {},
    { query: { enabled: !!subdomain } },
  );

  // Get pages for the default site
  const { data: pages, isLoading: pagesLoading } = useGetPages(
    platform?.default_site?.id || '',
    {},
    { query: { enabled: !!platform?.default_site?.id } },
  );

  // Get navigation items for the default site
  const { data: navigationItems, isLoading: navigationItemsLoading } = useGetNavigationItems(
    platform?.default_site?.id || '',
    { order: 'order' },
    { query: { enabled: !!platform?.default_site?.id } },
  );

  useEffect(() => {
    if (!platformLoading && !pagesLoading && !navigationItemsLoading && platform && pages) {
      const siteSlug = platform.default_site?.slug || '';

      // Look for a home page first
      const homePage = Page.getPageBySlug(pages || [], 'home');

      if (homePage) {
        // Redirect to home page
        navigate(`/${siteSlug}/${homePage.slug}`);
      } else {
        // If no home page is available, check the navigation items
        const internalNavItem = navigationItems?.find(
          (item) => item.internal && item.internal_link_object?.slug,
        );

        if (internalNavItem?.internal_link_object?.slug) {
          navigate(`/${siteSlug}/${internalNavItem.internal_link_object.slug}`);
        } else if (pages && pages.length > 0) {
          // If no useable navigation items are found, fall back to the first page found
          // random but better than nothing
          navigate(`/${siteSlug}/${pages[0].slug}`);
        }
      }
    }
  }, [
    platform,
    pages,
    navigationItems,
    platformLoading,
    pagesLoading,
    navigationItemsLoading,
    navigate,
  ]);

  return <JunoSpin />;
};

export default DefaultRedirect;
